import { useEffect, useRef, useState, useCallback } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { api, getStoredToken } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Real-time Executive Operations Snapshot with SSE + polling fallback.
 *
 * - Attempts SSE via `fetch-event-source` (supports Authorization header).
 * - Falls back to `/snapshot` polling every `pollIntervalMs` on any error.
 * - Exposes `connectionMode` ("sse" | "polling" | "connecting" | "error").
 */
export default function useOperationsSnapshot(view = "admin", pollIntervalMs = 20_000) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [connectionMode, setConnectionMode] = useState("connecting");
  const pollTimerRef = useRef(null);
  const abortRef = useRef(null);
  const failCountRef = useRef(0);
  const cancelledRef = useRef(false);

  const pollOnce = useCallback(async () => {
    try {
      const { data } = await api.get(`/executive/operations/snapshot?view=${view}`);
      setSnapshot(data); setError(null);
    } catch (e) { setError(e); }
  }, [view]);

  const startPolling = useCallback(() => {
    if (cancelledRef.current) return;
    setConnectionMode("polling");
    pollOnce();
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(pollOnce, pollIntervalMs);
  }, [pollOnce, pollIntervalMs]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  }, []);

  const startSse = useCallback(() => {
    if (cancelledRef.current) return;
    setConnectionMode("connecting");
    const token = getStoredToken() || "";
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    fetchEventSource(`${BACKEND}/api/executive/operations/stream?view=${view}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (response.ok && response.headers.get("content-type")?.includes("text/event-stream")) {
          setConnectionMode("sse"); failCountRef.current = 0; stopPolling();
        } else {
          throw new Error(`SSE handshake failed: ${response.status}`);
        }
      },
      onmessage(ev) {
        if (ev.event === "snapshot") {
          try {
            setSnapshot(JSON.parse(ev.data));
            setError(null);
          } catch {
            // Malformed message from server — ignore and let the next tick refresh.
          }
        }
      },
      onerror(err) {
        failCountRef.current += 1;
        if (failCountRef.current >= 2) {
          setError(err);
          startPolling();      // fall back permanently
          throw err;           // stop the auto-retry inside fetchEventSource
        }
      },
    }).catch(() => { /* onerror already handled fallback */ });
  }, [view, startPolling, stopPolling]);

  useEffect(() => {
    cancelledRef.current = false;
    startSse();
    return () => {
      cancelledRef.current = true;
      if (abortRef.current) abortRef.current.abort();
      stopPolling();
    };
  }, [view, startSse, stopPolling]);

  return { snapshot, error, connectionMode, refresh: pollOnce };
}
